/**
 * Fragrance Notes — scoped section interactions
 * Supports multiple instances via [data-fragrance-notes]
 */
(function () {
  'use strict';

  var STAR_SVG =
    '<svg class="fn__tab-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M7 0C7 3.86599 10.134 7 14 7C10.134 7 7 10.134 7 14C7 10.134 3.86599 7 0 7C3.86599 7 7 3.86599 7 0Z" fill="white"/></svg>';

  var HEIGHT_MS = 350;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isDesktopLayout() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  function FragranceNotes(root) {
    this.root = root;
    this.index = 0;
    this.showFlower = root.getAttribute('data-show-flower') === 'true';
    this.sectionFlowerUrl = root.getAttribute('data-section-flower-url') || '';

    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-fn-tab]'));
    this.desktopPanels = Array.prototype.slice.call(root.querySelectorAll('[data-fn-desktop-panel]'));
    this.mobilePanels = Array.prototype.slice.call(root.querySelectorAll('[data-fn-mobile-panel]'));
    this.desktopMedia = Array.prototype.slice.call(root.querySelectorAll('[data-fn-desktop-media]'));
    this.flowerWrap = root.querySelector('[data-fn-flower]');
    this.flowerImg = root.querySelector('[data-fn-flower-img]');

    this.onTabClick = this.onTabClick.bind(this);
    this.onTabKeydown = this.onTabKeydown.bind(this);

    this.bind();
    this.setActive(0, { instant: true });
  }

  FragranceNotes.prototype.bind = function () {
    var self = this;
    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', self.onTabClick);
      tab.addEventListener('keydown', self.onTabKeydown);
    });
  };

  FragranceNotes.prototype.onTabClick = function (event) {
    var idx = parseInt(event.currentTarget.getAttribute('data-fn-index'), 10);
    if (isNaN(idx)) return;
    this.setActive(idx);
  };

  FragranceNotes.prototype.onTabKeydown = function (event) {
    var key = event.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') return;

    event.preventDefault();
    var next = this.index;
    if (key === 'ArrowLeft') next = Math.max(this.index - 1, 0);
    if (key === 'ArrowRight') next = Math.min(this.index + 1, this.tabs.length - 1);
    if (key === 'Home') next = 0;
    if (key === 'End') next = this.tabs.length - 1;

    this.setActive(next);
    if (this.tabs[next]) this.tabs[next].focus();
  };

  FragranceNotes.prototype.getFlowerUrl = function (index) {
    var panel = this.desktopPanels[index] || this.mobilePanels[index];
    if (!panel) return '';

    var cardFlower = panel.getAttribute('data-flower-url') || '';
    if (cardFlower) return cardFlower;
    if (this.showFlower && this.sectionFlowerUrl) return this.sectionFlowerUrl;
    return '';
  };

  FragranceNotes.prototype.updateFlower = function (index) {
    if (!this.flowerWrap || !this.flowerImg) return;

    var url = this.getFlowerUrl(index);
    if (!url) {
      this.flowerWrap.setAttribute('hidden', '');
      this.flowerImg.removeAttribute('src');
      return;
    }

    if (this.flowerImg.getAttribute('src') !== url) {
      this.flowerImg.setAttribute('src', url);
    }
    this.flowerWrap.removeAttribute('hidden');
  };

  FragranceNotes.prototype.scrollTabIntoView = function (tab) {
    if (!tab || !tab.parentElement) return;
    if (prefersReducedMotion()) return;

    try {
      tab.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
    } catch (e) {
      tab.scrollIntoView(true);
    }
  };

  FragranceNotes.prototype.getStage = function () {
    return isDesktopLayout()
      ? this.root.querySelector('.fn__desktop-panels')
      : this.root.querySelector('.fn__mobile-stack');
  };

  FragranceNotes.prototype.getVisiblePanels = function () {
    return isDesktopLayout() ? this.desktopPanels : this.mobilePanels;
  };

  FragranceNotes.prototype.measurePanel = function (panel) {
    if (!panel) return 0;

    panel.removeAttribute('hidden');
    panel.setAttribute('data-fn-measuring', '');
    var height = panel.offsetHeight;
    panel.removeAttribute('data-fn-measuring');
    panel.setAttribute('hidden', '');
    return height;
  };

  FragranceNotes.prototype.clearStage = function (stage) {
    if (!stage) return;

    if (stage.__fnHeightTimer) {
      window.clearTimeout(stage.__fnHeightTimer);
      stage.__fnHeightTimer = null;
    }

    stage.style.height = '';
    stage.style.overflow = '';
  };

  FragranceNotes.prototype.beginSwitch = function (targetIndex) {
    if (prefersReducedMotion()) return;

    var stage = this.getStage();
    var panels = this.getVisiblePanels();
    var targetPanel = panels[targetIndex];
    if (!stage || !targetPanel) return;

    this.clearStage(stage);

    var currentHeight = stage.offsetHeight;
    var targetHeight = this.measurePanel(targetPanel);
    var lockHeight = Math.max(currentHeight, targetHeight);

    stage.style.overflow = 'hidden';
    stage.style.height = lockHeight + 'px';
  };

  FragranceNotes.prototype.endSwitch = function () {
    if (prefersReducedMotion()) return;

    var stage = this.getStage();
    if (!stage) return;

    var self = this;
    window.requestAnimationFrame(function () {
      stage.style.height = stage.offsetHeight + 'px';
      stage.__fnHeightTimer = window.setTimeout(function () {
        self.clearStage(stage);
      }, HEIGHT_MS);
    });
  };

  FragranceNotes.prototype.setActive = function (index, options) {
    options = options || {};
    if (index < 0) index = 0;
    if (index >= this.tabs.length) index = this.tabs.length - 1;

    var previousIndex = this.index;
    var switching = !options.instant && !prefersReducedMotion() && index !== previousIndex;

    if (switching) {
      this.beginSwitch(index);
    }

    this.index = index;

    this.tabs.forEach(function (tab, idx) {
      var active = idx === index;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');

      var icon = tab.querySelector('.fn__tab-icon');
      if (active && !icon) {
        tab.insertAdjacentHTML('afterbegin', STAR_SVG);
      } else if (!active && icon) {
        icon.remove();
      }
    });

    this.desktopPanels.forEach(function (panel, idx) {
      var active = idx === index;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('hidden', !active);
    });

    this.mobilePanels.forEach(function (panel, idx) {
      var active = idx === index;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('hidden', !active);
    });

    this.desktopMedia.forEach(function (item, idx) {
      item.classList.toggle('is-active', idx === index);
    });

    this.updateFlower(index);

    /* Skip on instant boot — scrollIntoView during PDP load jumps the page
       to this section (often read as landing on Creator Reels above it). */
    if (!options.instant && this.tabs[index]) {
      this.scrollTabIntoView(this.tabs[index]);
    }

    if (switching) {
      var self = this;
      window.setTimeout(function () {
        self.endSwitch();
      }, 0);
    }
  };

  function initAll() {
    document.querySelectorAll('[data-fragrance-notes]').forEach(function (root) {
      if (root.__fnInit) return;
      root.__fnInit = true;
      new FragranceNotes(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root =
      event.target && event.target.querySelector
        ? event.target.querySelector('[data-fragrance-notes]')
        : null;
    if (root && !root.__fnInit) {
      root.__fnInit = true;
      new FragranceNotes(root);
    }
  });
})();
